import { Button, Card, Checkbox, FormControlLabel, Radio, RadioGroup, Switch, TextField } from '@mui/material';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import 'react-datepicker/dist/react-datepicker.css';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { packageHandler } from '../../../redux/handlers';
import { DefaultPackageModal } from '../modals/DefaultPackageModal';
import { DeleteConfirmModal } from '../modals/DeleteConfirmModal';
import { PackageSuccessModal } from '../modals/PackageSuccessModal';

export function PackageMainTab() {
  const { packageID } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);

  const [initialFormData, setInitialFormData] = useState({
    name: '',
    price_month: '',
    price_year: '',
    is_not_show_customer: false,
    permissions: {},
    packageMainPermissions: []
  });

  const [modalDefaultPackage, setModalDefaultPackage] = useState({
    open: false,
    x: 0,
    y: 0,
    width: 460,
    height: 400,
    pin: false,
    mode: 'normal',
    modalname: 'create'
  });

  const [modalCreateSuccess, setModalCreateSuccess] = useState({
    open: false,
    x: 0,
    y: 0,
    width: 460,
    height: 350,
    pin: false,
    mode: 'normal',
    modalname: 'create'
  });

  const [modalDeleteConfirm, setModalDeleteConfirm] = useState({
    open: false,
    x: 0,
    y: 0,
    width: 460,
    height: 350,
    pin: false,
    mode: 'normal',
    modalname: 'delete'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const permissionsResult = await dispatch(packageHandler.getPermissions('MAIN'));
        if (permissionsResult) {
          setPermissions(permissionsResult);
        }

        if (packageID) {
          const packageResult = await dispatch(packageHandler.getPackageById(packageID));

          if (packageResult) {
            const formattedPermissions = {};

            packageResult.packageMainPermissions.forEach(mainPerm => {
              const permissionData = mainPerm.permission;
              console.log('permissionData : ', permissionData);

              formattedPermissions[mainPerm.permission_id] = {
                ...(permissionData.permission.qty !== undefined && {
                  qty: permissionData.permission.is_infinite ? '' : permissionData.permission.qty?.toString(),
                  is_infinite: permissionData.permission.is_infinite
                }),

                ...(permissionData.permission.value !== undefined && {
                  value: permissionData.permission.value
                })
              };
            });

            setInitialFormData({
              name: packageResult.name,
              price_month: packageResult.price_month?.toString(),
              price_year: packageResult.price_year?.toString(),
              is_not_show_customer: packageResult.is_not_show_customer,
              is_package_default: packageResult.is_package_default,
              permissions: formattedPermissions,
              packageMainPermissions: packageResult.packageMainPermissions
            });
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [dispatch, packageID]);

  const transformAmountPermission = permissionData => ({
    qty: permissionData.is_infinite ? null : permissionData.qty ? Number(permissionData.qty) : 0,
    is_infinite: permissionData.is_infinite || false
  });

  const transformChoicePermission = permissionData => ({
    value: permissionData.value || 'BASIC'
  });

  const transformBooleanPermission = permissionData => ({
    value: permissionData.value !== undefined ? permissionData.value : false
  });

  const transformPermission = (permission, values, packageMainPermissions = []) => {
    const currentPermission = values.permissions[permission.id] || {};
    let permissionData = {};

    const existingPermission = packageMainPermissions.find(p => p.permission_id === permission.id);

    switch (permission.permission.type) {
      case 'AMOUNT':
        permissionData = transformAmountPermission(currentPermission);
        break;
      case 'CHOICE':
        permissionData = transformChoicePermission(currentPermission);
        break;
      case 'BOOLEAN':
        permissionData = transformBooleanPermission(currentPermission);
        break;
      default:
        permissionData = {};
    }

    return {
      permission_id: permission.id,
      ...(existingPermission && { id: existingPermission.id }),
      permission: permissionData
    };
  };

  const transformFormToApiData = (values, permissions, packageMainPermissions = []) => {
    const transformedPermissions = permissions.map(perm => transformPermission(perm, values, packageMainPermissions));

    return {
      name: values.name,
      price_month: Number(values.price_month),
      price_year: Number(values.price_year),
      is_not_show_customer: values.is_not_show_customer,
      permissions: transformedPermissions
    };
  };

  const handleSubmit = async values => {
    try {
      const requestData = transformFormToApiData(
        values,
        permissions,
        packageID ? initialFormData.packageMainPermissions : []
      );

      let result;
      if (packageID) {
        result = await dispatch(packageHandler.updateMainPermission(packageID, requestData));
      } else {
        result = await dispatch(packageHandler.createPackage(requestData));
      }

      if (result) {
        setModalCreateSuccess({
          ...modalCreateSuccess,
          open: true
        });
        setTimeout(() => {
          setModalCreateSuccess({
            ...modalCreateSuccess,
            open: false
          });
          navigate('/packages');
        }, 1000);
      }
    } catch (error) {
      console.error(packageID ? 'Failed to update package:' : 'Failed to create package:', error);
    }
  };

  const handleDelete = async () => {
    try {
      let result = await dispatch(packageHandler.deletePackage(packageID));
      if (result) {
        setModalCreateSuccess({
          ...modalCreateSuccess,
          open: true
        });
        setTimeout(() => {
          setModalCreateSuccess({
            ...modalCreateSuccess,
            open: false
          });
          navigate('/packages');
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to delete package:', error);
    }
  };

  const handleRemove = () => {
    setModalDeleteConfirm({
      ...modalDeleteConfirm,
      open: true
    });
  };

  const handleDefaultPackageChange = async (newValue, setFieldValue) => {
    try {
      if (packageID) {
        const result = await dispatch(packageHandler.updatePackageDefault(packageID));

        if (result) {
          setFieldValue('is_package_default', newValue);

          setModalDefaultPackage({ ...modalDefaultPackage, open: false });
        }
      } else {
        setFieldValue('is_package_default', newValue);
        setModalDefaultPackage({ ...modalDefaultPackage, open: false });
      }
    } catch (error) {
      console.error('Failed to update package default status:', error);
    }
  };

  const basicPermissions = permissions?.filter(p => p.permission.type === 'AMOUNT');
  const featurePermissions = permissions?.filter(p => p.permission.type !== 'AMOUNT');

  const renderAmountPermission = (permission, values, handleChange, setFieldValue) => (
    <div
      key={permission.id}
      className="flex items-center px-4 py-2 border-b-[1px] border-[#4C4E64]/[0.12] text-[#4C4E64]/[0.87]"
    >
      <div className="w-[375px]">{permission.permissionName}</div>
      <div className="w-[200px]">
        <TextField
          label=""
          variant="outlined"
          InputProps={{ sx: { borderRadius: '7px' } }}
          fullWidth
          disabled={values?.permissions?.[permission.id]?.is_infinite}
          onChange={e => {
            const value = e.target.value;
            if (value === '' || /^\d+$/.test(value)) {
              setFieldValue(`permissions.${permission.id}.qty`, value);
            }
          }}
          name={`permissions.${permission.id}.qty`}
          value={values?.permissions?.[permission.id]?.qty || ''}
          type="text"
          inputProps={{
            pattern: '[0-9]*',
            inputMode: 'numeric'
          }}
        />
      </div>
      <div className="pl-3 pr-5 w-[80px]">/{permission.permission.unit}</div>
      <div>
        <FormControlLabel
          control={<Checkbox />}
          label="ไม่จำกัด"
          checked={values?.permissions?.[permission.id]?.is_infinite || false}
          onChange={e => {
            setFieldValue(`permissions.${permission.id}.is_infinite`, e.target.checked);
            if (e.target.checked) {
              setFieldValue(`permissions.${permission.id}.qty`, '');
            }
          }}
        />
      </div>
    </div>
  );

  const renderChoicePermission = (permission, values, setFieldValue) => (
    <div
      key={permission.id}
      className="flex items-center px-4 py-2 border-b-[1px] border-[#4C4E64]/[0.12] text-[#4C4E64]/[0.87]"
    >
      <div className="w-[375px]">{permission.permissionName}</div>
      <div className="">
        <RadioGroup
          row
          value={values?.permissions?.[permission.id]?.value || 'BASIC'}
          onChange={e => setFieldValue(`permissions.${permission.id}.value`, e.target.value)}
        >
          {permission.permission.choices.map(choice => (
            <FormControlLabel key={choice.value} value={choice.value} control={<Radio />} label={choice.name} />
          ))}
        </RadioGroup>
      </div>
    </div>
  );

  const renderBooleanPermission = (permission, values, setFieldValue) => (
    <div
      key={permission.id}
      className="flex items-center px-4 py-2 border-b-[1px] border-[#4C4E64]/[0.12] text-[#4C4E64]/[0.87]"
    >
      <div className="w-[375px]">{permission.permissionName}</div>
      <div className="">
        <RadioGroup
          row
          value={values?.permissions?.[permission.id]?.value ? '1' : '0'}
          onChange={e => setFieldValue(`permissions.${permission.id}.value`, e.target.value === '1')}
        >
          <FormControlLabel value="1" control={<Radio />} label="อนุญาต" />
          <FormControlLabel value="0" control={<Radio />} label="ไม่อนุญาต" />
        </RadioGroup>
      </div>
    </div>
  );

  return (
    <>
      <Card className="!rounded-lg mb-4">
        <div className="p-4">
          <div className="text-[#4C4E64] font-semibold text-xl mb-4">{packageID ? 'แก้ไขแพ็คเกจ' : 'สร้างแพ็คเกจ'}</div>
          <Formik enableReinitialize initialValues={initialFormData} onSubmit={handleSubmit}>
            {({ handleSubmit, handleChange, values, setFieldValue }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <div className="grid grid-cols-12 gap-4 items-center mb-7">
                  <div className="md:col-span-3 col-span-6">
                    <TextField
                      label="ชื่อแพ็คเกจ*"
                      variant="outlined"
                      InputProps={{ sx: { borderRadius: '7px' } }}
                      fullWidth
                      onChange={handleChange}
                      name="name"
                      value={values?.name || ''}
                    />
                  </div>
                  <div className="md:col-span-3 col-span-6">
                    <TextField
                      label="ราคาแพ็คเกจ (รายเดือน)"
                      variant="outlined"
                      InputProps={{ sx: { borderRadius: '7px' } }}
                      fullWidth
                      onChange={e => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          setFieldValue('price_month', value);
                        }
                      }}
                      name="price_month"
                      value={values?.price_month || ''}
                      type="text"
                      inputProps={{
                        pattern: '[0-9]*',
                        inputMode: 'numeric'
                      }}
                    />
                  </div>
                  <div className="md:col-span-3 col-span-6">
                    <TextField
                      label="ราคาแพ็คเกจ (รายปี)"
                      variant="outlined"
                      InputProps={{ sx: { borderRadius: '7px' } }}
                      fullWidth
                      onChange={e => {
                        const value = e.target.value;
                        if (value === '' || /^\d+$/.test(value)) {
                          setFieldValue('price_year', value);
                        }
                      }}
                      name="price_year"
                      value={values?.price_year || ''}
                      type="text"
                      inputProps={{
                        pattern: '[0-9]*',
                        inputMode: 'numeric'
                      }}
                    />
                  </div>
                  <div className="md:col-span-3 col-span-6">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values?.is_not_show_customer || false}
                          onChange={e => setFieldValue('is_not_show_customer', e.target.checked)}
                        />
                      }
                      label="ไม่แสดงในฝั่งลูกค้า"
                    />
                  </div>
                  {packageID && (
                    <div className="md:col-span-12 col-span-12">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={values?.is_package_default || false}
                            onChange={async e => {
                              const newValue = e.target.checked;
                              if (newValue) {
                                const defaultPackages = await dispatch(packageHandler.getPackageDefaults());
                                if (defaultPackages) {
                                  setModalDefaultPackage({
                                    ...modalDefaultPackage,
                                    open: true
                                  });
                                  return;
                                }
                              }

                              handleDefaultPackageChange(newValue, setFieldValue);
                            }}
                          />
                        }
                        label="ตั้งเป็นแพ็คเกจเริ่มต้น"
                      />
                    </div>
                  )}
                </div>

                <div className="text-[#4C4E64] font-semibold text-lg p-4">ตั้งค่าแพ็คเกจ</div>

                <div className="bg-[#4C4E64]/[0.12] text-[#4C4E64]/[0.87] px-4 py-3">คุณสมบัติพื้นฐาน</div>
                {basicPermissions.map(permission =>
                  renderAmountPermission(permission, values, handleChange, setFieldValue)
                )}

                <div className="bg-[#4C4E64]/[0.12] text-[#4C4E64]/[0.87] px-4 py-3 mt-4">ฟีเจอร์</div>
                {featurePermissions.map(permission =>
                  permission.permission.type === 'CHOICE'
                    ? renderChoicePermission(permission, values, setFieldValue)
                    : renderBooleanPermission(permission, values, setFieldValue)
                )}

                <div className="flex justify-between mt-4">
                  {packageID && (
                    <Button
                      type="button"
                      className="btn-st-5 me-3 !bg-[#F95057] !rounded-lg !text-[#FFFFFF]"
                      variant="outlined"
                      onClick={handleRemove}
                    >
                      ลบ
                    </Button>
                  )}
                  <div className="flex justify-end w-full">
                    <Button
                      type="submit"
                      className="btn-st-3 !bg-[#409FFB] !rounded-lg !text-white"
                      variant="contained"
                    >
                      บันทึก
                    </Button>
                  </div>
                </div>
                <DeleteConfirmModal
                  modal={modalDeleteConfirm}
                  setModal={setModalDeleteConfirm}
                  onConfirm={handleDelete}
                />
                <PackageSuccessModal modal={modalCreateSuccess} setModal={setModalCreateSuccess} />
                <DefaultPackageModal
                  modal={modalDefaultPackage}
                  setModal={setModalDefaultPackage}
                  onConfirm={() => handleDefaultPackageChange(true, setFieldValue)}
                />
              </Form>
            )}
          </Formik>
        </div>
      </Card>
    </>
  );
}
